/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { BiLoaderAlt } from "react-icons/bi";
import { MdOutlineTimeline } from "react-icons/md";
import { HiDownload, HiChevronDown } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import {
  trackingList,
  trackingExport,
} from "../../../../stores/actions/tracking.actions.types";
import Search from "../../../Common/Search";
import Sorting from "../../../Common/Sorting";
import Paginations from "../../../Common/Pagination";
import MetaHead from "../../../Common/Metahead";
import DateFilter, { getDateParams } from "../../../Common/DateFilter";

const METADATA_LABELS = {
  productId: "Product ID",
  productName: "Product",
  price: "Price",
  quantity: "Qty",
  ymmData: "YMM",
  query: "Query",
  element: "Element",
  cartTotal: "Cart Total",
  couponCode: "Coupon",
  orderId: "Order ID",
};

const renderYMM = (ymm) => {
  const fields = [
    "year",
    "make",
    "model",
    "bodyType",
    "subModel",
    "boltPattern",
    "frontRimSize",
    "rearRimSize",
  ];
  const tireSizes = ymm.tireSizes
    ? [...new Set(ymm.tireSizes.map((t) => t.front))].join(" · ")
    : null;

  return (
    <div className='rounded-md border border-white/10 overflow-hidden text-xs w-[500px]'>
      <div className='px-2 py-1.5 grid grid-cols-3 gap-1'>
        {fields.map((key) =>
          ymm[key] ? (
            <span
              key={key}
              className='inline-flex items-center gap-1 rounded dark:bg-white/5 bg-gray-100 border dark:border-white/10 border-gray-100 px-1.5 py-0.5'
            >
              <span className='text-gray-600 dark:text-gray-300 capitalize text-[10px]'>
                {key.replace(/([A-Z])/g, " $1").trim()} :
              </span>
              <span className=' font-medium'>{ymm[key]}</span>
            </span>
          ) : null,
        )}
        {tireSizes && (
          <span className='inline-flex items-start gap-1 rounded dark:bg-white/5 bg-gray-100 border dark:border-white/10 border-gray-100 px-1.5 py-0.5 w-full'>
            <span className='text-gray-600 dark:text-gray-300 text-[10px] whitespace-nowrap'>
              Tire Sizes
            </span>
            <span className=' font-medium font-mono'>{tireSizes}</span>
          </span>
        )}
      </div>
    </div>
  );
};

const renderCheckoutProducts = (products) => {
  if (!Array.isArray(products) || !products.length) return null;

  return (
    <div className='rounded-md border border-white/10 overflow-hidden text-xs w-[500px]'>
      <div className='px-2 py-1.5 flex flex-col gap-1'>
        {products.map((product, index) => (
          <div
            key={index}
            className='grid grid-cols-3 gap-1 rounded dark:bg-white/5 bg-gray-100 border dark:border-white/10 border-gray-100 px-1.5 py-1'
          >
            {[
              {
                label: "Title",
                val: product.title ?? product?.name ?? "Product Name",
                mono: true,
              },
              {
                label: "Price",
                val:
                  product.price != null ? `$${product.price.toFixed(2)}` : null,
                mono: true,
              },
              { label: "Qty", val: product.quantity, mono: true },
            ]
              .filter((f) => f.val != null)
              .map(({ label, val, mono }) => (
                <span
                  key={label}
                  className='flex items-center gap-1 rounded bg-white dark:bg-black/20 border dark:border-white/10 border-gray-200 px-1.5 py-0.5'
                >
                  <span className='text-gray-600 dark:text-gray-300 w-[50px] '>
                    {label} :
                  </span>
                  <span
                    className={`font-medium ${mono ? "font-mono" : ""} truncate text-[10px]`}
                  >
                    {val}
                  </span>
                </span>
              ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const renderMetadata = (metadata) => {
  if (!metadata || !Object.keys(metadata).length)
    return <span className='dark:text-gray-300 text-gray-700'>—</span>;
  const entries = Object.entries(metadata).filter(
    ([, val]) => val !== null && val !== undefined && val !== "",
  );
  if (!entries.length)
    return <span className='dark:text-gray-300 text-gray-700'>—</span>;

  return (
    <div className='flex  gap-1'>
      {entries.map(([key, val]) => {
        if (key === "ymmData" && typeof val === "object") {
          return (
            <div key={key} className='flex flex-col gap-1'>
              <span className='text-[10px] dark:text-gray-500 text-black uppercase tracking-wider font-medium px-0.5'>
                YMM
              </span>
              {renderYMM(val)}
            </div>
          );
        }
        if (key === "checkoutProducts" && Array.isArray(val)) {
          return (
            <div key={key} className='flex flex-col gap-1'>
              <span className='text-[10px] dark:text-gray-500 text-black uppercase tracking-wider font-medium px-0.5'>
                Checkout Products
              </span>
              {renderCheckoutProducts(val)}
            </div>
          );
        }

        const label = METADATA_LABELS[key] || key;
        const display =
          typeof val === "object" ? JSON.stringify(val) : String(val);
        const isNumeric = !isNaN(Number(display)) && display.trim() !== "";

        return (
          <span
            key={key}
            className='inline-flex items-stretch rounded-md border dark:border-white/10 border-gray-100 overflow-hidden text-xs w-fit'
          >
            <span className='px-2 py-0.5 bg-white/5  font-normal border-r border-white/10 whitespace-nowrap'>
              {label}
            </span>
            <span
              className={`px-2 py-0.5 font-medium ${isNumeric ? "font-mono" : ""}`}
            >
              {display}
            </span>
          </span>
        );
      })}
    </div>
  );
};

const eventTypeValues = [
  { label: "All", value: "" },
  { label: "Page View", value: "page_view" },
  { label: "Product View", value: "product_view" },
  { label: "Add to Cart", value: "add_to_cart" },
  { label: "Remove from Cart", value: "remove_from_cart" },
  { label: "Update Cart", value: "update_cart" },
  { label: "Search", value: "search" },
  { label: "Click", value: "click" },
  { label: "Checkout Complete", value: "checkout_complete" },
];

const sourceValues = [
  { label: "All Sources", value: "" },
  { label: "Google Ads", value: "google_ads" },
  { label: "Facebook", value: "facebook" },
  { label: "Meta", value: "meta" },
  { label: "Microsoft Ads", value: "microsoft_ads" },
  { label: "Organic", value: "organic" },
  { label: "Direct", value: "direct" },
  { label: "Email", value: "email" },
  { label: "Referral", value: "referral" },
  { label: "TikTok", value: "tiktok" },
  { label: "Twitter", value: "twitter" },
];

const eventTypeBadge = {
  page_view: "bg-blue-100 text-blue-700",
  product_view: "bg-purple-100 text-purple-700",
  add_to_cart: "bg-green-100 text-green-700",
  remove_from_cart: "bg-red-100 text-red-700",
  update_cart: "bg-yellow-100 text-yellow-700",
  search: "bg-gray-100 text-gray-700",
  click: "bg-orange-100 text-orange-700",
  checkout_complete: "bg-emerald-100 text-emerald-700",
};

const TrackingEventsList = (props) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [eventType, setEventType] = useState("");
  const [source, setSource] = useState("");
  const [dateParams, setDateParams] = useState(getDateParams("today"));
  const [events, setEvents] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [isDark, setIsDark] = useState(
    document.documentElement.classList.contains("dark"),
  );
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) {
        setExportOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    getList(search, pagination.page, eventType, source, dateParams);
  }, [search, pagination.page, eventType, source, dateParams]);

  const getList = (
    q = search,
    page = pagination.page,
    type = eventType,
    src = source,
    dp = dateParams,
  ) => {
    const callback = (data) => {
      if (data.success) {
        const { events, total, pages } = data.data;
        setPagination((prev) => ({
          ...prev,
          total: total || 0,
          pages: pages || 1,
        }));
        setEvents(events || []);
      }
    };
    const params = { q, page, limit: 10, ...dp };
    if (type) params.eventType = type;
    if (src) params.source = src;
    props.trackingList({ callback, data: params });
  };

  const handleEventTypeChange = (val) => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setEventType(val);
  };

  const handleSourceChange = (val) => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSource(val);
  };

  const handleDateFilterChange = (params) => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setDateParams(params);
  };

  const handleExport = (format) => {
    setExporting(true);
    const params = { format, ...dateParams };
    if (search) params.q = search;
    if (eventType) params.eventType = eventType;
    if (source) params.source = source;

    props.trackingExport({
      data: params,
      callback: ({ success, blob, format: fmt }) => {
        setExporting(false);
        if (success && blob) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
          link.download = `tracking_events_${timestamp}.${fmt === "excel" ? "xlsx" : "csv"}`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }
      },
    });
  };

  return (
    <div>
      <MetaHead title='Tracking Events' />
      <div className='flex flex-col sm:flex-row flex-wrap items-center justify-end mb-3 gap-2'>
        <Search
          search={search}
          placeholder='Search by email or path'
          setSearch={(val) => {
            setPagination((prev) => ({ ...prev, page: 1 }));
            setSearch(val);
          }}
        />
        <Sorting
          name='Event Type'
          values={eventTypeValues}
          onChange={handleEventTypeChange}
        />
        <Sorting
          name='Source'
          values={sourceValues}
          onChange={handleSourceChange}
        />
        <DateFilter onChange={handleDateFilterChange} />
        <div className='relative' ref={exportRef}>
          <button
            onClick={() => setExportOpen((prev) => !prev)}
            disabled={exporting}
            className='inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-boxdark text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          >
            {exporting ? (
              <BiLoaderAlt className='animate-spin' size={15} />
            ) : (
              <HiDownload size={15} />
            )}
            Export
            <HiChevronDown
              size={14}
              className={`transition-transform ${exportOpen ? "rotate-180" : ""}`}
            />
          </button>
          {exportOpen && (
            <div className='absolute right-0 mt-1 w-36 rounded border border-gray-200 dark:border-gray-600 bg-white dark:bg-boxdark shadow-lg z-10'>
              <button
                onClick={() => {
                  setExportOpen(false);
                  handleExport("csv");
                }}
                className='w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
              >
                Export as CSV
              </button>
              <button
                onClick={() => {
                  setExportOpen(false);
                  handleExport("excel");
                }}
                className='w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
              >
                Export as Excel
              </button>
            </div>
          )}
        </div>
      </div>

      {props.loading ? (
        <div className='flex dark:bg-boxdark bg-white items-center justify-center min-h-96'>
          <BiLoaderAlt className='animate-spin text-4xl text-primary dark:text-white' />
        </div>
      ) : events?.length > 0 ? (
        <>
          <div className='rounded-[10px] bg-white border border-stroke p-4 dark:bg-boxdark'>
            <div className='max-w-full overflow-x-auto'>
              <table className='w-full table-auto'>
                <thead>
                  <tr className='bg-[#F7F9FC] dark:bg-black text-left'>
                    <th className='min-w-[160px] px-4 py-2 font-medium text-sm'>
                      Email
                    </th>
                    <th className='min-w-[130px] px-4 py-2 font-medium text-sm'>
                      Phone
                    </th>

                    <th className='min-w-[140px] px-4 py-2 font-medium text-sm'>
                      Event Type
                    </th>
                    <th className='min-w-[200px] px-4 py-2 font-medium text-sm'>
                      Path
                    </th>
                    <th className='min-w-[200px] px-4 py-2 font-medium text-sm'>
                      Metadata
                    </th>
                    <th className='min-w-[140px] px-4 py-2 font-medium text-sm'>
                      Session ID
                    </th>
                    <th className='min-w-[160px] px-4 py-2 font-medium text-sm'>
                      Timestamp
                    </th>
                    <th className='px-4 py-2 font-medium text-sm sticky right-0 bg-[#F7F9FC] dark:bg-black'>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((item, index) => (
                    <tr key={index} className='text-sm'>
                      <td className='border-b px-4 py-2 dark:border-strokedark'>
                        {item.email || <span className='text-gray-400'>—</span>}
                      </td>
                      <td className='border-b px-4 py-2 dark:border-strokedark'>
                        {item.phone || <span className='text-gray-400'>—</span>}
                      </td>

                      <td className='border-b px-4 py-2 dark:border-strokedark'>
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            eventTypeBadge[item.eventType] ||
                            "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {item.eventType}
                        </span>
                      </td>
                      <td className='border-b px-4 py-2 dark:border-strokedark max-w-[200px] truncate'>
                        {item.path || <span className='text-gray-400'>—</span>}
                      </td>
                      <td className='border-b px-4 py-2 dark:border-strokedark'>
                        {renderMetadata(item.metadata)}
                      </td>
                      <td className='border-b px-4 py-2 dark:border-strokedark font-mono text-xs truncate max-w-[140px]'>
                        {item.sessionId}
                      </td>
                      <td className='border-b px-4 py-2 dark:border-strokedark'>
                        {new Date(item.timestamp).toLocaleString()}
                      </td>
                      <td className='border-b px-4 py-2 dark:border-strokedark sticky right-0 bg-white dark:bg-boxdark'>
                        <button
                          disabled={!item.email && !item.phone}
                          onClick={() =>
                            navigate(
                              item.email
                                ? `/tracking/journey?email=${encodeURIComponent(item.email)}`
                                : `/tracking/journey?phone=${encodeURIComponent(item.phone)}`,
                            )
                          }
                          title={
                            item.email
                              ? `View journey for ${item.email}`
                              : item.phone
                                ? `View journey for ${item.phone}`
                                : "No contact information available"
                          }
                          className='inline-flex items-center justify-center w-8 h-8 rounded-md bg-red-50 dark:bg-red-900 text-red-600 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors'
                        >
                          <MdOutlineTimeline size={17} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Paginations pagination={pagination} setPagination={setPagination} />
        </>
      ) : (
        <div className='flex dark:bg-boxdark rounded bg-white items-center justify-center min-h-96'>
          <p className='text-xl text-center text-primary'>No Events Found</p>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  loading: state.app.visible,
});

export default connect(mapStateToProps, { trackingList, trackingExport })(
  TrackingEventsList,
);
